/**
 * Guardas para las respuestas de `req()` (api.ts).
 *
 * `req()` devuelve los 422 SIN lanzar, y está bien que así sea: varias pantallas leen `code`,
 * `faltantes` y `ambiguos` para decidir qué mostrar. El costo es que un llamador que no mira la
 * respuesta da por bueno un rechazo: la baja inválida vaciaba el input sin decir nada, y la base
 * de seguimiento rechazada borraba los archivos elegidos como si se hubiera medido.
 *
 * Funciones puras, sin imports de api.ts, para poder testearlas sin fetch ni auth.
 */

type Respuesta = {
  status?: number;
  success?: boolean;
  error?: string;
  code?: string;
  faltantes?: string[];
  ambiguos?: { campo: string; candidatos?: string[] }[];
  errores?: string[];
};

/**
 * Devuelve la respuesta si salió bien; si el backend la rechazó (success:false o status >= 400),
 * lanza un Error con el mensaje del backend, y el status y el cuerpo colgados para quien los necesite.
 */
export function exigirOk<T extends Respuesta>(r: T): T {
  if (r?.success === false || (r?.status ?? 200) >= 400) {
    throw Object.assign(new Error(r?.error || "No se pudo completar la acción"), { status: r?.status, body: r });
  }
  return r;
}

/**
 * El texto para un 422 de mapeo de archivos (análisis y seguimiento). Cada código pide algo
 * distinto: confirmar columnas ambiguas, corregir un mapeo inválido o agregar columnas que faltan.
 * Si el backend no mandó el detalle suelto, se usa su mensaje: una frase con la lista vacía
 * («Faltan datos mínimos: ») no le dice al operador qué hacer.
 */
export function mensaje422Mapeo(r: Respuesta): string {
  if (r?.code === "REQUIERE_CONFIRMACION") {
    return "Confirmá qué columna es cada dato antes de seguir: " + (r.ambiguos || []).map((a) => `${a.campo} (¿${(a.candidatos || []).join(" o ")}?)`).join("; ");
  }
  if (r?.code === "MAPEO_INVALIDO") return "Hay columnas mal asignadas: " + (r.errores || []).join("; ");
  if (r?.faltantes?.length) return "Faltan datos mínimos: " + r.faltantes.join(", ");
  return r?.error || "No se pudo completar la acción";
}
